import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { HeaderComponent } from "@components/header/header.component";
import _ from "lodash";
import { Subject, merge } from "rxjs";
import { takeUntil, tap, map } from "rxjs/operators";
import { RuleErrorContext, RuleSeverityLevel, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VKAtgardMbf } from "~app/features/virkeskop/models/mbf-models";
import { AtgardMbfService } from "~app/features/virkeskop/services/atgard-mbf.service";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { ForyngringsatgardComponent } from "./foryngringsatgard/foryngringsatgard.component";
import { MarkberedningsmetodComponent } from "./markberedningsmetod/markberedningsmetod.component";
import { canEdit } from "~app/role-permissions";
@Component({
    selector: "rask-atgard-mbf",
    templateUrl: "./atgard-mbf.component.html",
    styleUrls: ["./atgard-mbf.component.scss"],
    providers: [AtgardMbfService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        MarkberedningsmetodComponent,
        ForyngringsatgardComponent
    ]
})
export class AtgardMbfComponent implements OnInit {
    atgardMbf: VKAtgardMbf;

    protected kontraktReadonly = false;

    private readonly atgardMbfService = inject(AtgardMbfService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly ngDestroyed$ = new Subject<void>();
    private readonly virkeskopValidationService = inject(VirkeskopValidationService);

    get atgard() {
        return this.atgardMediatorService.atgard;
    }

    private get kontrakt() {
        return this.kontraktMediatorService.kontrakt;
    }

    private readonly canEdit = canEdit();

    ngOnInit(): void {
        const atgardMbf$ = this.atgardMbfService.atgardMbf$.pipe(
            tap((mbf) =>  this.atgardMbf = new VKAtgardMbf(mbf))
        );

        merge(
            atgardMbf$,
            this.atgardMbfService.anmalanTradslagChanged$,
            this.atgardMediatorService.nettoareal$
        ).pipe(
            takeUntil(this.ngDestroyed$)
        ).subscribe(() => this.validate());

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngDestroyed$)).subscribe(readonly => this.kontraktReadonly = readonly);
    }

    private validate() {
        if(ObjectUtils.isNullOrUndefined(this.atgardMbf)) {
            return;
        }

        let valid = true;
        if(this.atgardMbf.anmalanTradslag?.length > 0) {
            const totalArea = this.atgardMbfService.getTotalArea(this.atgardMbf.anmalanTradslag);
            const nettoareal = _.round(this.atgard.nettoareal, 1);
            valid = totalArea === nettoareal;
        }

        this.virkeskopValidationService.updateClientSideValidation(!valid, {
            severity: RuleSeverityLevel.error,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save,
            entityType: "atgard",
            entityGuids: [
                this.kontrakt.guid,
                this.atgard.guid
            ],
            message: "Den sammanlagda föryngringsytan ska överensstämma med åtgärdens nettoareal",
            messageId: "anmalanTradslagNettoareal"
        });
    }
}
